import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Box, Typography, List, ListItem, ListItemText, Link } from '@mui/material';

function InfoPage() {
  
  // --- SEO: UPDATE TITLE & DESCRIPTION ---
  useEffect(() => {
    document.title = "Christmas Events, Carols & Santa Info | Christmas Lights Map";

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "Find Christmas Eve Carols by Candlelight locations, where to see Santa Claus in 2025, and Boxing Day event guides. Your complete Australian holiday planner.");
    }

    return () => {
      document.title = "Christmas Lights Map 2025";
    };
  }, []);

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          The Magic of the Holiday Season
        </Typography>

        <Typography variant="body1" paragraph>
          Our map isn't just a list of locations; it's a guide to the magic of the holiday season. Here's your guide to the major events happening in December 2025.
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Events in the Lead-Up to Christmas
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Christmas Markets"
              secondary="Inspired by European tradition, these markets are filled with mulled wine, handmade crafts, and festive food. Look for major markets in The Rocks (Sydney), Queen Victoria Market (Melbourne), and South Bank (Brisbane)."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Tree Lighting Ceremonies"
              secondary="From the giant spruce at Rockefeller Center in New York to the beautiful tree in Sydney's Martin Place, the official lighting of the city tree is a major event that kicks off the season in late November or early December."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Spectacular Light Shows"
              secondary="Many landmarks use incredible light projections. Don't miss the Melbourne Town Hall projections or the 'Lights of Christmas' at St Mary's Cathedral in Sydney."
            />
          </ListItem>
        </List>

        {/* --- NEW: CHRISTMAS EVE & CAROLS SECTION --- */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Christmas Eve & Carols by Candlelight
        </Typography>
        <Typography variant="body1" paragraph>
          Christmas Eve (24th December) is the night of anticipation! Across Australia, families gather on picnic rugs to sing carols under the stars.
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Vision Australia’s Carols by Candlelight (Melbourne)
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Location:</strong> Sidney Myer Music Bowl, Melbourne.<br />
          <strong>Date:</strong> Christmas Eve, December 24th.<br />
          <strong>Time:</strong> Gates open at 4:00 PM, main show starts at 8:00 PM.<br />
          <strong>Details:</strong> This is Australia's most famous carols event, broadcast live on Channel 9. Tickets sell out months in advance, but many families watch it live on TV or listen on the radio while driving around looking at lights!
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Woolworths Carols in the Domain (Sydney)
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Location:</strong> The Domain, Royal Botanic Gardens, Sydney.<br />
          <strong>Date:</strong> Usually the Saturday before Christmas (Check local guides for 2025 date).<br />
          <strong>Details:</strong> Australia's largest free Christmas concert, featuring top Australian artists and a visit from Santa.
        </Typography>
        {/* --------------------------------------- */}

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Christmas Day (25th December)
        </Typography>
        <Typography variant="body1" paragraph>
          A day celebrated by millions worldwide. In Australia, it often involves a seafood lunch, backyard cricket, and a trip to the beach! It is the one day of the year when the world slows down, filled with the joy of giving and sharing.
        </Typography>

        {/* --- UPDATED BOXING DAY SECTION --- */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Boxing Day (26th December)
        </Typography>
        <Typography variant="body1" paragraph>
          While its origins are debated (from giving "boxes" of gifts to servants, to church collection boxes), Boxing Day is now known for two things: relaxing with leftovers and enjoying major sporting events! In Australia, it's famous for the <strong>Boxing Day Test</strong> (Cricket) at the MCG and the start of the <strong>Sydney to Hobart Yacht Race</strong>.
        </Typography>
        {/* ---------------------------------- */}

        {/* --- NEW: SANTA SECTION --- */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Where to Find Santa Claus 🎅
        </Typography>
        <Typography variant="body1" paragraph>
          Santa is busy in December, but you can find him at these popular locations for a photo before he heads back to the North Pole:
        </Typography>
        <ul>
          <li>
            <strong>Myer Santaland:</strong> Found in major Myer department stores in Melbourne (Bourke St), Sydney (George St), Brisbane, and Perth. <em>(Tip: Book online to skip the queue!)</em>
          </li>
          <li>
            <strong>David Jones Magic Cave:</strong> A tradition in Adelaide and other major David Jones flagship stores.
          </li>
          <li>
            <strong>Queen Victoria Building (QVB), Sydney:</strong> Often features a stunning crystal tree and a dedicated Santa photo area under the dome.
          </li>
          <li>
            <strong>The "Santa Run" (Local Suburbs):</strong> In many Australian suburbs (especially in regional areas like Ballarat), the local <strong>CFA or Fire Brigade</strong> drives Santa around the streets on a fire truck, throwing lollies to kids. Check your local CFA Facebook page for dates and routes!
          </li>
        </ul>
        {/* -------------------------- */}

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          How to Find Christmas Lights Near You
        </Typography>
        <Typography variant="body1" paragraph>
          Looking for the best <strong>Christmas lights near me</strong>? Our interactive christmas map is here to help! You can use our "Find My Location" button to center the map on your position, or simply click on the colored pins to see photos and addresses.
        </Typography>
        <Typography variant="body1" paragraph>
          You can also <strong>plan your Christmas route</strong> right from the map. Just click on any display you want to visit (or a random spot on the map) to add it to your route. When you're ready, click "Start Navigation" to send all the stops to Google Maps on your phone!
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Find Lights Near You - The 2025 Map
        </Typography>
        <Typography variant="body1" paragraph>
          Our goal is to be the best <strong>Christmas Lights Map for 2025</strong>. 
          This is a community-driven map of <strong>festive neighbourhoods</strong> and 
          user-submitted <strong>community christmas displays</strong>. You can find the <strong>best christmas lights in Sydney</strong>, <strong>Melbourne</strong>, <strong>Brisbane</strong>, <strong>Perth</strong>, <strong>Adelaide</strong>, <strong>Canberra</strong>, and <strong>Hobart</strong>.
        </Typography>
        <Typography variant="body1" paragraph>
          We also feature famous international Christmas locations in <strong>New York</strong>, <strong>London</strong>, <strong>Paris</strong>, and <strong>Tokyo</strong>. 
          Add your display today to join the global celebration! 
          
          <Link component={RouterLink} to="/" sx={{ ml: 1, fontWeight: 'bold' }}>
            Check out the map now!
          </Link>
        </Typography>

        <Box sx={{ mt: 6, textAlign: 'center', borderTop: '1px solid #ddd', pt: 4 }}>
          <Typography variant="h5" component="p" gutterBottom sx={{ fontStyle: 'italic' }}>
            Merry Christmas 2025 and Have a Happy New Year.
          </Typography>
          <Typography variant="h6" component="p">
            We will see you in 2026!
          </Typography>
        </Box>
        
      </Box>
    </Container>
  );
}

export default InfoPage;